import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Upload } from 'lucide-react';
import { useCreateExpenseItem } from '../../lib/resources/mutations';
import { parseError } from '../../lib/api/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

// Mirror of backend CreateExpenseItemRequest validation:
//   label @NotBlank @Size(max=200), amountCents @Min(0).
// amountString is the input field — converted to cents at submit time so the
// user can type "20.00" instead of "2000".
const expenseFormSchema = z.object({
  label: z.string().min(1, 'Required').max(200, 'Too long'),
  amountString: z.string().regex(/^\d+(\.\d{1,2})?$/, 'e.g. 20 or 20.00'),
});
type ExpenseFormValues = z.infer<typeof expenseFormSchema>;

interface ExpenseAddFormProps {
  periodId: number;
  submissionId: number;
  onFile: (file: File | null) => void;
  uploading: boolean;
  uploadError: string | null;
}

export function ExpenseAddForm({ periodId, submissionId, onFile, uploading, uploadError }: ExpenseAddFormProps) {
  const createItem = useCreateExpenseItem(periodId);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: { label: '', amountString: '' },
  });
  const labelError = form.formState.errors.label?.message;
  const amountError = form.formState.errors.amountString?.message;

  async function onSubmit(values: ExpenseFormValues) {
    setServerError(null);
    const amountCents = Math.round(Number(values.amountString) * 100);
    try {
      await createItem.mutateAsync({ submissionId, label: values.label.trim(), amountCents });
      form.reset({ label: '', amountString: '' });
    } catch (e) {
      const err = await parseError(e);
      setServerError(err.message || 'Add failed');
    }
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (uploading) return;
    setIsDragging(true);
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf') onFile(file);
  }

  return (
    <div className="mt-4 space-y-3">
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex gap-2 items-start">
        <div className="flex-1">
          <Label htmlFor="item-label">Label</Label>
          <Input id="item-label" placeholder="OpenAI Pro" {...form.register('label')} />
          {labelError && <div className="text-[11px] text-error mt-1">{labelError}</div>}
        </div>
        <div className="w-32">
          <Label htmlFor="item-amount">Amount</Label>
          <Input
            id="item-amount"
            inputMode="decimal"
            placeholder="20.00"
            {...form.register('amountString')}
          />
          {amountError && <div className="text-[11px] text-error mt-1">{amountError}</div>}
        </div>
        <div className="pt-[22px]">
          <Button type="submit" disabled={createItem.isPending} size="md">
            <Plus className="size-3.5" /> Add
          </Button>
        </div>
      </form>

      {serverError && (
        <div className="text-[12px] text-error" role="alert">{serverError}</div>
      )}

      <label
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={[
          'flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed rounded-[--radius-input] cursor-pointer transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground-faint',
          uploading ? 'opacity-50 cursor-wait' : '',
        ].join(' ')}
      >
        <input
          type="file"
          accept="application/pdf"
          className="sr-only"
          disabled={uploading}
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <Upload className="size-5 text-foreground-muted" />
        <div className="text-[12px] text-foreground-muted">
          {uploading
            ? 'Uploading…'
            : isDragging
            ? 'Drop PDF here'
            : 'Drop a PDF here, or click to browse'}
        </div>
        <div className="text-[11px] text-foreground-muted">PDF only, attached as a receipt to this month</div>
      </label>

      {uploadError && (
        <div className="text-[12px] text-error" role="alert">{uploadError}</div>
      )}
    </div>
  );
}
